import { useEffect, useState } from 'react';
import type { ConsumerDeductFromRentPreviewModel } from './failed-payments.types';

type ApiClient = {
  post: <T>(url: string, body: unknown) => Promise<T>;
};

type UseDeductFromRentPreviewArgs = {
  apiClient: ApiClient;
  balanceUniqueIds: string[];
  enabled: boolean;
};

export function useDeductFromRentPreview({
  apiClient,
  balanceUniqueIds,
  enabled,
}: UseDeductFromRentPreviewArgs) {
  const [preview, setPreview] =
    useState<ConsumerDeductFromRentPreviewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const balancesKey = balanceUniqueIds.join(',');

  useEffect(() => {
    if (!enabled || !balancesKey) {
      setPreview(null);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    (async () => {
      try {
        const response =
          await apiClient.post<ConsumerDeductFromRentPreviewModel>(
            '/v2/users/failed-payments/deduct-from-rent/preview',
            { balanceUniqueIds: balancesKey.split(',') },
          );
        if (!cancelled) {
          setPreview(response);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPreview(null);
          setError(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiClient, balancesKey, enabled]);

  return { preview, loading, error };
}
